import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Search, Linkedin, ChevronLeft, ChevronRight, UserPlus, Building2, X } from 'lucide-react';
import { toast } from 'sonner';
import { fetchWithAuth } from './IntelligenceLayout';
import { ApolloEnrichPanel } from './ApolloEnrichPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BATCH_SIZE = 25;

const EMPTY_FORM = { first_name: '', last_name: '', title: '', email: '', phone: '', linkedin_url: '', notes: '' };

const ResearchLinks = ({ links }) => (
  <div className="flex flex-wrap gap-2 mt-2">
    {links.map(l => (
      <React.Fragment key={l.title}>
        <a href={l.linkedin_search_url} target="_blank" rel="noreferrer" data-testid={`enrich-linkedin-link-${l.title.replace(/\s+/g, '-')}`}
          className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md bg-[#0A66C2]/10 text-[#0A66C2] hover:bg-[#0A66C2]/20 transition-colors">
          <Linkedin className="h-3 w-3" /> Search "{l.title}"
        </a>
        <a href={l.google_search_url} target="_blank" rel="noreferrer" data-testid={`enrich-google-link-${l.title.replace(/\s+/g, '-')}`}
          className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors">
          <Search className="h-3 w-3" /> Google
        </a>
      </React.Fragment>
    ))}
  </div>
);

const EnrichCard = ({ row, onResolved, onOpenCompany }) => {
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM, title: row.target_titles[0] || '' });
  const [saving, setSaving] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const org = row.apollo_org_snapshot;

  const save = async () => {
    if (!form.first_name.trim()) { toast.error('First name is required'); return; }
    if (!form.email.trim() && !form.phone.trim() && !form.linkedin_url.trim()) {
      toast.error('Add at least one usable outreach method: email, phone, or LinkedIn'); return;
    }
    setSaving(true);
    try {
      const payload = { first_name: form.first_name.trim(), last_name: form.last_name.trim() || null, title: form.title.trim() || null,
        email: form.email.trim() || null, phone: form.phone.trim() || null, linkedin_url: form.linkedin_url.trim() || null,
        is_decision_maker: true, notes: form.notes.trim() || null };
      if (row.existing_contact) {
        await fetchWithAuth(`${API_URL}/api/crm/contacts/${row.existing_contact.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      } else {
        await fetchWithAuth(`${API_URL}/api/crm/contacts`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...payload, company_id: row.id }) });
      }
      toast.success(`${row.name} moved to Ready to Contact`);
      onResolved(row.id);
    } catch (e) {
      toast.error('Could not save contact');
    } finally {
      setSaving(false);
    }
  };

  if (skipped) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4" data-testid={`enrich-card-${row.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => onOpenCompany(row.id)} data-testid={`enrich-open-company-${row.id}`} className="font-semibold text-[#0A1F44] hover:underline text-sm">{row.name}</button>
            <span className="text-xs text-gray-400">{[row.industry, row.location].filter(Boolean).join(' • ')}</span>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200">{row.contact_readiness}</span>
          </div>
          {row.existing_contact ? (
            <p className="text-xs text-gray-600 mt-1.5">Role identified on file: <b>{row.existing_contact.title}</b> — real name/contact not yet found</p>
          ) : (
            <p className="text-xs text-gray-600 mt-1.5">No decision-maker role identified yet — try one of these titles:</p>
          )}
          <ResearchLinks links={row.research_links} />
          {org ? (
            <div className="mt-2 flex items-center gap-3 text-[11px] text-gray-500 bg-gray-50 rounded-lg px-2.5 py-1.5">
              <Building2 className="h-3 w-3 flex-shrink-0" />
              <span>{org.domain}{org.employee_count ? ` · ~${org.employee_count.toLocaleString()} employees` : ''}{org.industry ? ` · ${org.industry}` : ''}</span>
              {org.linkedin_url && <a href={org.linkedin_url} target="_blank" rel="noreferrer" className="text-[#0A66C2] hover:underline">Company LinkedIn</a>}
            </div>
          ) : (
            <p className="mt-2 text-[11px] text-gray-400">No reliable Apollo company data available for this domain.</p>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-sm font-bold text-[#0A1F44]" title={row.priority_reasons.join('; ')}>{row.priority_score}</div>
          <div className="text-[10px] text-gray-400">priority score</div>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-3">
        <button onClick={() => setExpanded(!expanded)} data-testid={`enrich-toggle-found-${row.id}`}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-[#0A1F44] text-white hover:bg-[#0A1F44]/90">
          <UserPlus className="h-3.5 w-3.5" /> {expanded ? 'Cancel' : 'Found the contact?'}
        </button>
        <button onClick={() => setSkipped(true)} data-testid={`enrich-skip-${row.id}`} className="px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700">
          Skip for now
        </button>
      </div>

      <ApolloEnrichPanel companyId={row.id} onEnriched={() => onResolved(row.id)} />

      {expanded && (
        <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 gap-2" data-testid={`enrich-form-${row.id}`}>
          <input placeholder="First name *" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })}
            data-testid={`enrich-first-name-${row.id}`} className="text-xs px-2 py-1.5 border border-gray-200 rounded-lg" />
          <input placeholder="Last name" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })}
            data-testid={`enrich-last-name-${row.id}`} className="text-xs px-2 py-1.5 border border-gray-200 rounded-lg" />
          <input placeholder="Title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
            data-testid={`enrich-title-${row.id}`} className="text-xs px-2 py-1.5 border border-gray-200 rounded-lg col-span-2" />
          <input placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
            data-testid={`enrich-email-${row.id}`} className="text-xs px-2 py-1.5 border border-gray-200 rounded-lg" />
          <input placeholder="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
            data-testid={`enrich-phone-${row.id}`} className="text-xs px-2 py-1.5 border border-gray-200 rounded-lg" />
          <input placeholder="LinkedIn URL" value={form.linkedin_url} onChange={e => setForm({ ...form, linkedin_url: e.target.value })}
            data-testid={`enrich-linkedin-${row.id}`} className="text-xs px-2 py-1.5 border border-gray-200 rounded-lg col-span-2" />
          <textarea placeholder="Research notes (where you found this)" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
            data-testid={`enrich-notes-${row.id}`} rows={2} className="text-xs px-2 py-1.5 border border-gray-200 rounded-lg col-span-2 resize-none" />
          <button onClick={save} disabled={saving} data-testid={`enrich-save-${row.id}`}
            className="col-span-2 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Save & Mark Ready to Contact
          </button>
        </div>
      )}
    </div>
  );
};

const CRMEnrichmentQueue = ({ onOpenCompany }) => {
  const [data, setData] = useState({ queue: [], total_remaining: 0, skip: 0 });
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);

  const load = useCallback(() => {
    setLoading(true);
    fetchWithAuth(`${API_URL}/api/crm/opportunities/enrichment-queue?limit=${BATCH_SIZE}&skip=${skip}`)
      .then(setData).catch(() => toast.error('Could not load enrichment queue')).finally(() => setLoading(false));
  }, [skip]);

  useEffect(() => { load(); }, [load]);

  const handleResolved = (companyId) => {
    setData(d => ({ ...d, queue: d.queue.filter(r => r.id !== companyId), total_remaining: Math.max(0, d.total_remaining - 1) }));
  };

  const batchNum = Math.floor(skip / BATCH_SIZE) + 1;
  const totalBatches = Math.max(1, Math.ceil(data.total_remaining / BATCH_SIZE));

  return (
    <div className="space-y-4" data-testid="crm-enrichment-queue">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-sm font-medium text-[#0A1F44]">Batch {batchNum} of {totalBatches} — {data.total_remaining.toLocaleString()} companies still need a real decision-maker</p>
          <p className="text-xs text-gray-500 mt-0.5">Search Apollo directly for each company, or use the manual research links if Apollo doesn't have a match.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setSkip(Math.max(0, skip - BATCH_SIZE))} disabled={skip === 0} data-testid="enrich-prev-batch"
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 disabled:opacity-40 hover:border-[#0A1F44]/40">
            <ChevronLeft className="h-3.5 w-3.5" /> Prev
          </button>
          <button onClick={() => setSkip(skip + BATCH_SIZE)} disabled={skip + BATCH_SIZE >= data.total_remaining} data-testid="enrich-next-batch"
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 disabled:opacity-40 hover:border-[#0A1F44]/40">
            Next <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      ) : data.queue.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm" data-testid="enrichment-queue-empty">Nothing left in this batch — great work.</div>
      ) : (
        <div className="space-y-3">
          {data.queue.map(row => <EnrichCard key={row.id} row={row} onResolved={handleResolved} onOpenCompany={onOpenCompany} />)}
        </div>
      )}
    </div>
  );
};

export default CRMEnrichmentQueue;
